// Aufnahme-Hinweise für die Anrufliste (Spalte „Aufnahme“): jede Zeile fragt nach ihrem Hinweis, gesammelt geht es als eine Abfrage
// an `GET /api/gespraeche/aufnahmen`. Antworten bleiben kurz im Speicher (Aufnahmen laufen nach 7 Tagen ab, deshalb nicht ewig).
import { useEffect, useState } from "react";
import { gespraecheApi, type GespraechAufnahme } from "../api/gespraeche";
import { beimNutzerwechsel } from "../daten/sitzung";

const GUELTIG_MS = 5 * 60_000;
const SAMMELN_MS = 40;
const PAKET = 100;

const speicher = new Map<string, { hinweis: GespraechAufnahme; stand: number }>();
const wartend = new Set<string>();
const unterwegs = new Set<string>();
const hoerer = new Map<string, Set<() => void>>();
let timer: number | undefined;

beimNutzerwechsel(() => {
  speicher.clear();
  wartend.clear();
});

function melden(id: string): void {
  hoerer.get(id)?.forEach((h) => h());
}

async function abholen(): Promise<void> {
  timer = undefined;
  const ids = [...wartend];
  wartend.clear();
  for (let i = 0; i < ids.length; i += PAKET) {
    const paket = ids.slice(i, i + PAKET);
    paket.forEach((id) => unterwegs.add(id));
    try {
      const r = await gespraecheApi.aufnahmen(paket);
      for (const id of paket) {
        const hinweis = r.data[id];
        if (hinweis) speicher.set(id, { hinweis, stand: Date.now() });
      }
    } catch {
      // Ohne Antwort bleibt die Zelle leer; ein späterer Aufruf versucht es nochmal
    } finally {
      paket.forEach((id) => {
        unterwegs.delete(id);
        melden(id);
      });
    }
  }
}

function anfordern(id: string): void {
  if (wartend.has(id) || unterwegs.has(id)) return;
  wartend.add(id);
  if (timer === undefined) timer = window.setTimeout(() => void abholen(), SAMMELN_MS);
}

/** Hinweis zu einer Anruf-Aktivität; `undefined`, solange er noch geladen wird (oder `aktiv` falsch ist). */
export function useAufnahmeHinweis(id: string, aktiv = true): GespraechAufnahme | undefined {
  const [, setN] = useState(0);
  useEffect(() => {
    if (!aktiv) return;
    const h = () => setN((n) => n + 1);
    let satz = hoerer.get(id);
    if (!satz) hoerer.set(id, (satz = new Set()));
    satz.add(h);
    const e = speicher.get(id);
    if (!e || Date.now() - e.stand > GUELTIG_MS) anfordern(id);
    return () => {
      satz?.delete(h);
      if (satz && !satz.size) hoerer.delete(id);
    };
  }, [id, aktiv]);
  return aktiv ? speicher.get(id)?.hinweis : undefined;
}
